import type { SteerNote } from "../types/steer-types"

export type QueuedMessageSeed = { id: string; text: string }

export type SteerTurnEndSettlement = {
  /** Chips that should remain after the turn ends. */
  steerNotes: SteerNote[]
  /** Notes that missed pickup and should drain via the normal queue. */
  queuedMessagesToAppend: QueuedMessageSeed[]
}

/**
 * Issue #2469: settle composer steer chips when a turn ends.
 *
 * - `delivered` notes are dropped — confirmation lives in the feed bubble +
 *   Activity History, not a leftover composer chip.
 * - `pending` / `undelivered` miss the model: when the message queue is on,
 *   requeue them as normal queued messages (not Forced). When the queue is
 *   off, keep them as dismissable `undelivered` chips (no auto-drain).
 * - `rejected` chips always stay until the next turn resets state.
 */
export function settleSteerNotesAtTurnEnd(
  notes: SteerNote[],
  isMessageQueueEnabled: boolean
): SteerTurnEndSettlement {
  const rejected = notes.filter((note) => note.status === "rejected")
  const missed = notes.filter((note) => note.status === "pending" || note.status === "undelivered")

  if (isMessageQueueEnabled) {
    return {
      steerNotes: rejected,
      queuedMessagesToAppend: missed.map((note) => ({
        id: crypto.randomUUID(),
        text: note.content,
      })),
    }
  }

  return {
    steerNotes: [
      ...rejected,
      ...missed.map((note) =>
        note.status === "pending" ? { ...note, status: "undelivered" as const } : note
      ),
    ],
    queuedMessagesToAppend: [],
  }
}
